import * as React from 'react';
import NextImage from 'next/image';
import { NextSeo } from 'next-seo';
import { Type } from '@/analytics/client';
import { AppImageCard } from '@/components/collective/AppImageCard';
import { CollectiveOverview } from '@/components/collective/CollectiveOverview';
import { GreatPartner } from '@/components/collective/GreatPartner';
import { StartingSteps } from '@/components/collective/StartingSteps';
import { DownloadAppButton, Hero, SectionHeader } from '@/components/common';
import { FAQs } from '@/components/marketing';
import { Button, Divider } from '@/components/ui';
import { CarouselContent, CarouselItem, EmblaCarousel } from '@/components/ui/EmblaCarousel/EmblaCarousel';
import { useAnalyticsDispatch } from '@/context/analytics';
import { appImages } from '@/data/app-images';
import { useEpixelClick } from '@/hooks/useEpixelClick';
export const CollectivePage = React.memo(() => {
  const {
    buttonFunction,
    hasJoined
  } = useEpixelClick();
  const analyticsDispatch = useAnalyticsDispatch();

  function handleButtonClick() {
    analyticsDispatch({
      type: 'BUTTON_CLICKED',
      payload: {
        text: ctaText,
        location: 'collective',
        type: Type.Button
      }
    });
    buttonFunction();
  }

  const ctaText = hasJoined ? 'View Portal' : 'Join Now';
  return <>
      <NextSeo title='The 28 Wellness Collective' />
      <div className='space-y-10 md:space-y-16'>
        <div className='relative'>
          <Hero centerText copyFirst poster='/video/28-collective-hero-poster.jpeg' videoURL='https://stream.mux.com/HTHpHgXKsXZdXBc6VoSSdadfwowmSWdkHY5XnL6coH4/high.mp4' />
          <div className='absolute bottom-10 left-0 right-0 mx-auto text-khaki-1'>
            <div className='max-w-6xl mx-auto'>
              <div className='flex flex-col items-center'>
                <p className='text-xs mb-4 uppercase'>Become a part of</p>
                <h1 className='font-bodoni font-normal text-[40px] md:text-6xl leading-[100%] text-center'>
                  <span className='block md:inline'>The 28 Wellness</span>{' '}
                  <span className='block md:inline'>Collective</span>
                </h1>
                {ctaText && <Button className='mt-6 order-3' onClick={handleButtonClick} variant='primary'>
                    {ctaText}
                  </Button>}
              </div>
            </div>
          </div>
        </div>

        <div className='relative h-[160px] pt-16 md:pt-6'>
          <Divider className='my-2 md:visible lg:my-0' variant='wavy' />
          <div className='-top-8 self-center w-full flex justify-center absolute lg:top-0'>
            <NextImage alt='' className='object-cover pt-10 md:pt-2' height={160} src='/icons/flower.svg' width={160} />
          </div>
        </div>
        <p className='text-xl px-6 max-w-[1024px] text-center mx-auto lg:text-3xl'>
          The 28 Wellness Collective offers active members of 28 who are passionate about living in sync with their
          cycle a way to earn recurring income by building and fostering their own communities within 28.
        </p>
        <CollectiveOverview ctaText={ctaText} onClick={handleButtonClick} />

        <div className='relative'>
          <Hero centerText copyFirst onClick={handleButtonClick} poster='/video/28-collective-join-us-poster.jpeg' videoURL='https://stream.mux.com/01zvhmRZ2wx702kTS7sv7bHtlnzKJ5Ck0168X9PQT9LCqY/high.mp4' />
          <div className='absolute text-center bottom-20 left-0 right-0 font-bodoni font-normal leading-none tracking-tighter text-[40px] sm:text-4xl md:text-6xl lg:text-6xl mb-4 text-khaki-1 max-w-[450px] md:max-w-[800px] lg:max-w-[1200px] px-6 md:px-8 m-auto'>
            <p>Our Mission is a Movement,</p>
            <p className=''>Driven by Our Community</p>
            <div className='w-full flex justify-center'>
              <Button className='mt-6 self-center' onClick={handleButtonClick} variant='primary'>
                {ctaText}
              </Button>
            </div>
          </div>
        </div>

        <SectionHeader className='max-w-[820px]' description=' In just over a year, more than half a million women have joined 28 from around the world. Our incredible growth is entirely word of mouth. We don’t run ads. We don’t have billboards. But we do have shared stories of transformation from you, the women who use 28. These stories resonate across cities, countries, and oceans. We want to partner with and reward those who are at the heart of our growth.' headingChildren={<h3 className='font-lato text-2xl md:text-3xl uppercase tracking-tight font-bold bg-clip-text text-transparent bg-gradient-yellow-to-purple'>
              Why Did We Start This?
            </h3>} image='/icons/atoms.svg' />

        <div className='py-6 bg-khaki-1'>
          <SectionHeader className='max-w-[800px] pb-8' classNameHeading='tracking-tighter' description='' headingChildren={<h3 className='font-lato text-2xl md:text-3xl uppercase tracking-tight font-bold bg-clip-text text-transparent bg-gradient-yellow-to-purple'>
                How You Know You’ll
                <br />
                Make a Great Partner
              </h3>} image='/icons/atoms.svg' />
          <GreatPartner ctaText={ctaText} onClick={handleButtonClick} />
        </div>

        <div className='py-0'>
          <SectionHeader description=' Sign up and get started in 4 easy steps.' headingChildren={<h3 className='font-lato text-2xl md:text-3xl uppercase tracking-tight font-bold bg-clip-text text-transparent bg-gradient-yellow-to-purple'>
                It's easy to get started
              </h3>} image='/icons/atoms.svg' />
          <StartingSteps ctaText={ctaText} onClick={handleButtonClick} />
        </div>
        <div className='relative'>
          <Hero centerText copyFirst onClick={handleButtonClick} poster='/video/28-collective-beach-poster.png' videoURL='https://stream.mux.com/sPo81eBT02gsk00DEaTWjznlHCgiE2wC9Q013HQyXeqAtY/high.mp4' />
          <div className='absolute text-center bottom-20 left-0 right-0 font-bodoni font-normal leading-none tracking-tighter text-[40px] sm:text-4xl md:text-6xl lg:text-6xl mb-4 text-khaki-1 max-w-[450px] md:max-w-[800px] lg:max-w-[1200px] px-6 md:px-8 m-auto'>
            <p>Share the Secret Every Woman</p>
            <p className=''>Deserves to Know</p>
            <div className='w-full flex justify-center'>
              <Button className='mt-6 self-center' onClick={handleButtonClick} variant='primary'>
                {hasJoined ? 'View Portal' : 'Join Now'}
              </Button>
            </div>
          </div>
        </div>

        <div className='py-0'>
          <SectionHeader className='max-w-[800px]' description={"Our bodies are dynamic, ever-changing forces of nature, akin to the four seasons. For far too long, our health has been misunderstood, guided by standards rooted in male biology and the 24-hour hormone cycle of men, neglecting the fact that women's cycles are 28 times longer. Women's health and well-being are profoundly influenced by our hormonal fluctuations, which are regulated by our menstrual cycles. By working with our bodies, not against them—through exercise, nutrition, and lifestyle changes personalized to the fluctuations of our unique cycles—we can achieve hormonal balance naturally. This knowledge is transformative; once you embrace it, there's no turning back."} headingChildren={<h3 className='font-lato text-2xl md:text-3xl uppercase tracking-tight font-bold bg-clip-text text-transparent bg-gradient-yellow-to-purple'>
                What’s the Secret?
              </h3>} image='/icons/atoms.svg' />
          <div className='w-full flex justify-center'>
            <Button className='mt-6 self-center' onClick={handleButtonClick} variant='primary'>
              {hasJoined ? 'View Portal' : 'Join Now'}
            </Button>
          </div>
        </div>

        <div className='py-16 bg-khaki-1'>
          <div className='pb-10'>
            {appImages && <EmblaCarousel autoScroll opts={{
            loop: true
          }}>
                <CarouselContent className='-ml-3'>
                  {appImages.map(({
                image,
                alt
              }) => <CarouselItem className='pl-3 basis-1/2 md:basis-1/3 lg:basis-1/4' key={alt}>
                      <AppImageCard alt={alt} image={image} />
                    </CarouselItem>)}
                </CarouselContent>
              </EmblaCarousel>}
          </div>
          <SectionHeader className='max-w-[800px]' description='Loved by supermodels, college girls, and stay-at-home moms alike, the 28 app is #1 in the world for syncing your cycle. Built with patent-pending technology, it’s the first ever holistic wellness experience that combines everything - cycle tracking, fitness, nutrition, insights, and more - and customizes it all daily to your hormonal fluctuations.' headingChildren={<h3 className='font-lato text-2xl md:text-3xl uppercase tracking-tight font-bold bg-clip-text text-transparent bg-gradient-yellow-to-purple'>
                About Our App
              </h3>} />
          <div className='flex mb-6 gap-3 justify-center sm:items-center pt-8 px-6'>
            <DownloadAppButton location='collective' os='iOS' />
            <DownloadAppButton location='collective' os='Android' />
          </div>
        </div>

        <div className='py-16 '>
          <SectionHeader description='Our Most Frequently Asked Questions' title='F.A.Q.' />
          <FAQs network />
        </div>
      </div>
    </>;
});
CollectivePage.displayName = 'CollectivePage';
export default CollectivePage;